import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogoMark } from "@/components/Logo";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Globe, Menu, X } from "lucide-react";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { href: "/#services", label: t.nav.services, testid: "nav-services" },
    { href: "/#pricing", label: t.nav.pricing, testid: "nav-pricing" },
    { href: "/#about", label: t.nav.about, testid: "nav-about" },
    { href: "/#contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-3 md:top-5 left-0 right-0 z-50 px-3 md:px-6"
        data-testid="site-header"
      >
        <div
          className={`max-w-7xl mx-auto aq-glass aq-glass-pill flex items-center justify-between transition-all duration-300 ${
            scrolled ? "py-2 px-3 md:px-4" : "py-2.5 px-3 md:px-5"
          }`}
        >
          <Link to="/" data-testid="header-home-link" className="flex items-center gap-2.5 px-2">
            <LogoMark size={24} />
            <span className="font-serif-display text-[1.3rem] tracking-tight">Aqion</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 font-mono-aq text-[0.7rem] uppercase tracking-[0.18em]">
            {navItems.map((n) => (
              <a key={n.href} href={n.href} className="aq-nav-item" data-testid={n.testid}>
                <span>{n.label}</span>
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setLang(lang === "tr" ? "en" : "tr")}
              className="aq-nav-item flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.18em] font-mono-aq"
              data-testid="lang-switcher"
              aria-label="Toggle language"
            >
              <span className="flex items-center gap-1.5"><Globe size={13} /> {lang.toUpperCase()}</span>
            </button>

            {user ? (
              <>
                {user.role === "admin" && (
                  <NavLink to="/admin" className="aq-button-ghost hidden md:inline-flex !py-2 !px-4 !text-[0.68rem]" data-testid="nav-admin">
                    Admin
                  </NavLink>
                )}
                <NavLink to="/profile" className="aq-nav-item hidden md:flex" data-testid="nav-profile">
                  <span>{t.nav.profile}</span>
                </NavLink>
                <NavLink to="/dashboard" className="aq-button-ghost hidden sm:inline-flex !py-2 !px-4 !text-[0.68rem]" data-testid="nav-dashboard">
                  {t.nav.dashboard}
                </NavLink>
                <button
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="aq-button-primary !py-2 !px-4 !text-[0.68rem]"
                  data-testid="nav-logout"
                >
                  {t.nav.logout}
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" className="aq-button-ghost hidden sm:inline-flex !py-2 !px-4 !text-[0.68rem]" data-testid="nav-login">
                  {t.nav.login}
                </NavLink>
                <NavLink to="/register" className="aq-button-primary !py-2 !px-4 !text-[0.68rem]" data-testid="nav-register">
                  {t.nav.register}
                </NavLink>
              </>
            )}

            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden aq-nav-item ml-1"
              aria-label="Open menu"
              data-testid="mobile-menu-open"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Spacer */}
      <div className="h-16 md:h-20" />

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] aq-glass-dark flex flex-col p-6"
            data-testid="mobile-menu"
          >
            <div className="flex justify-end">
              <button onClick={() => setMobileOpen(false)} className="text-[#EAE4D5]" data-testid="mobile-menu-close"><X size={24} /></button>
            </div>
            <nav className="flex-1 flex flex-col items-center justify-center gap-7 font-serif-display text-[#EAE4D5] text-3xl">
              {navItems.map((n) => (
                <a key={n.href} href={n.href} onClick={() => setMobileOpen(false)} className="aq-link" data-testid={`mobile-${n.testid}`}>{n.label}</a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
